import { useEditorStore } from '../../state/editorStore';
import { PanelShell, SectionLabel, SliderRow } from '../controls/PanelShell';

const RESOLUTION_PRESETS = [
  { w: 1920, h: 1080, label: '1920×1080' },
  { w: 1280, h: 720, label: '1280×720' },
  { w: 3840, h: 2160, label: '3840×2160' },
  { w: 1080, h: 1080, label: '1080×1080' },
] as const;

function landscapePair(width: number, height: number): { w: number; h: number } {
  return width >= height ? { w: width, h: height } : { w: height, h: width };
}

export function CompositionPanel() {
  const settings = useEditorStore((s) => s.project.settings);
  const setComposition = useEditorStore((s) => s.setComposition);

  const isSquare = settings.width === settings.height;
  const isVertical = !isSquare && settings.height > settings.width;
  const base = landscapePair(settings.width, settings.height);
  const selectValue = `${base.w}x${base.h}`;
  const known = RESOLUTION_PRESETS.some((p) => p.w === base.w && p.h === base.h);

  return (
    <PanelShell title="Composition">
      <SectionLabel>Timeline Settings</SectionLabel>
      <label className="grid grid-cols-[88px_1fr] items-center gap-2 px-2 py-1 text-2xs">
        <span className="text-text-low">Resolution</span>
        <select
          className="border border-hairline bg-raised px-1 py-1"
          value={known ? selectValue : `${settings.width}x${settings.height}`}
          onChange={(e) => {
            const [w, h] = e.target.value.split('x').map(Number);
            if (!w || !h) return;
            if (isVertical && w !== h) {
              setComposition({ width: h, height: w });
            } else {
              setComposition({ width: w, height: h });
            }
          }}
        >
          {RESOLUTION_PRESETS.map((p) => (
            <option key={`${p.w}x${p.h}`} value={`${p.w}x${p.h}`}>
              {p.label}
            </option>
          ))}
          {!known && (
            <option value={`${settings.width}x${settings.height}`}>
              {settings.width}×{settings.height}
            </option>
          )}
        </select>
      </label>
      <label className="flex items-center gap-2 px-2 py-1 pl-[calc(88px+0.5rem)] text-2xs">
        <input
          type="checkbox"
          checked={isVertical}
          disabled={isSquare}
          title={
            isSquare
              ? 'Square resolutions cannot be rotated'
              : 'Swap width and height for vertical / portrait'
          }
          onChange={(e) => {
            if (isSquare) return;
            if (e.target.checked) {
              if (settings.width > settings.height) {
                setComposition({ width: settings.height, height: settings.width });
              }
            } else if (settings.height > settings.width) {
              setComposition({ width: settings.height, height: settings.width });
            }
          }}
        />
        <span className="text-text-low">Vertical Resolution</span>
      </label>
      <label className="grid grid-cols-[88px_1fr] items-center gap-2 px-2 py-1 text-2xs">
        <span className="text-text-low">Frame Rate</span>
        <select
          className="border border-hairline bg-raised px-1 py-1"
          value={settings.frameRate}
          onChange={(e) => setComposition({ frameRate: Number(e.target.value) })}
        >
          {[23.976, 24, 25, 29.97, 30, 50, 60].map((f) => (
            <option key={f} value={f}>
              {f} fps
            </option>
          ))}
        </select>
      </label>
      <SliderRow
        label="Duration"
        value={settings.duration}
        min={5}
        max={600}
        step={1}
        onChange={(duration) => setComposition({ duration })}
        format={(v) => `${v | 0}s`}
      />
      <label className="grid grid-cols-[88px_1fr] items-center gap-2 px-2 py-1 text-2xs">
        <span className="text-text-low">Background</span>
        <input
          type="color"
          value={settings.backgroundColor}
          onChange={(e) => setComposition({ backgroundColor: e.target.value })}
        />
      </label>
    </PanelShell>
  );
}
